//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { getDeetsItemDetails } from '@apis/content-library/contentLibraryApi';
import * as metricsApi from '@apis/metrics/metricsApi';
import {
  IItemProductStatsRequest,
  MediaInfoType,
  UnifiedMetricsItemProductStatsResponseContributorInfo,
  UnifiedMetricsItemProductStatsResponseItemInfo,
  UnifiedMetricsItemProductStatsResponseProductInfo,
} from '@apis/metrics/metricsApiModel';
import { TopPerformersContentType } from '@insights/insightsConstants';
import { IInsightsActivityStats } from '@insights/redux/insightsPageModel';
import Cuid from '@models/Cuid';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { UniversalItemType } from '@models/UniversalItemType';
import { isDefined } from '@utils/filterPredicates';
import { itemToPartialCuid } from '@utils/partialCuidUtils';
import getUrlParams from '@utils/urlUtils';

type UnifiedMetricsItemProductStatsResponseAll =
  | UnifiedMetricsItemProductStatsResponseItemInfo
  | UnifiedMetricsItemProductStatsResponseProductInfo
  | UnifiedMetricsItemProductStatsResponseContributorInfo;

const getCuid = (
  item: UnifiedMetricsItemProductStatsResponseAll,
): PartialCuid | undefined => {
  switch (item.mediaInfo.type) {
    case MediaInfoType.ProductInfo:
      // This is not a real partialCuid, but it's used to generate a unique id for the virtualized list.
      return itemToPartialCuid(
        UniversalItemType.Product,
        ItemId(item.mediaInfo.fileId ?? ''),
      );
    case MediaInfoType.ContributorInfo: {
      if (isDefined(item.mediaInfo.itemInfo?.cuid)) {
        const parsedCuid = Cuid.parse(item.mediaInfo.itemInfo.cuid);
        return PartialCuid.build({
          itemType: parsedCuid.itemType,
          id: parsedCuid.itemId,
        });
      }
      return undefined;
    }
    case MediaInfoType.ItemInfo: {
      const parsedCuid = Cuid.parse(item.mediaInfo.cuid);
      return PartialCuid.build({
        itemType: parsedCuid.itemType,
        id: parsedCuid.itemId,
      });
    }
  }
};

export const fetchApiActivityStats = async (
  contentType: TopPerformersContentType,
  itemProductStatsRequest: IItemProductStatsRequest,
  activityStats: IInsightsActivityStats,
): Promise<IInsightsActivityStats> => {
  let itemStatsRes;
  switch (contentType) {
    case TopPerformersContentType.MEDIA: {
      itemStatsRes = await metricsApi.getItemStats(itemProductStatsRequest);
      break;
    }
    case TopPerformersContentType.CONTRIBUTORS: {
      itemStatsRes = await metricsApi.getContributorStats(
        itemProductStatsRequest,
      );
      break;
    }
    case TopPerformersContentType.SHOWROOM: {
      itemStatsRes = await metricsApi.getSourceStats(itemProductStatsRequest);
      break;
    }
    case TopPerformersContentType.REVEALS: {
      itemStatsRes = await metricsApi.getItemSourceStats(
        itemProductStatsRequest,
      );
      break;
    }
    case TopPerformersContentType.PRODUCT:
    default: {
      itemStatsRes = await metricsApi.getProductStats(itemProductStatsRequest);
      break;
    }
  }

  // Fetch Item Details and build result
  const partialcuids =
    contentType === TopPerformersContentType.CONTRIBUTORS ||
    contentType === TopPerformersContentType.PRODUCT
      ? []
      : itemStatsRes.map((item) => getCuid(item)).filter(isDefined);

  const itemDetailsRes =
    partialcuids.length > 0
      ? await getDeetsItemDetails(partialcuids)
      : undefined;

  const itemStatsWithDetails = itemStatsRes.map((item, index) => {
    const partialCuid = partialcuids[index];

    return {
      stats: item,
      details: isDefined(partialCuid)
        ? itemDetailsRes?.[partialCuid]
        : undefined,
    };
  });

  return {
    ...activityStats,
    itemStats: itemStatsWithDetails,
  };
};

export const getTestVariationFromParams = () => {
  const testVariationParam = getUrlParams().testVariation;
  return isDefined(testVariationParam)
    ? decodeURIComponent(testVariationParam)
    : undefined;
};
